/**
 * RegenerateChannelKitDialog — small modal shown when the user clicks
 * "↻ Regenerate" on an existing channel kit card. Lets them attach (or
 * change) the cast from their Character Library before re-running
 * banner + avatar generation, so they don't have to delete the kit
 * and rebuild from scratch just to add characters.
 *
 * When the user has no characters in the library yet, the dialog
 * just shows a hint + link to /characters and falls through to a
 * plain "Regenerate" confirmation.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  listCharacters,
  characterImageUrl,
} from "@/lib/charactersApi";
import StylePresetPicker from "@/components/StylePresetPicker";

export default function RegenerateChannelKitDialog({ kit, onClose, onConfirm }) {
  const [characters, setCharacters] = useState([]);
  const [picked, setPicked] = useState(() => kit?.character_ids || []);
  const [stylePreset, setStylePreset] = useState(
    () => kit?.style_preset || "pixar_3d",
  );
  const [bannerProvider, setBannerProvider] = useState(
    () => kit?.banner_provider || "openai",
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listCharacters()
      .then((list) => setCharacters(list || []))
      .catch(() => setCharacters([]))
      .finally(() => setLoading(false));
  }, []);

  const togglePick = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const pickedSummary = useMemo(() => {
    if (!picked.length) return null;
    const names = characters
      .filter((c) => picked.includes(c.character_id))
      .map((c) => c.name);
    return names.join(", ");
  }, [picked, characters]);

  const submit = async () => {
    setSubmitting(true);
    try {
      // onConfirm callers accept either (characterIds) or
      // ({ characterIds, stylePreset }). We pass both positional +
      // named so old ChannelKitDetail wiring still works while the
      // newer callers can consume the style change too.
      await onConfirm(picked, stylePreset, bannerProvider);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="regenerate-dialog"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-zinc-950 border border-zinc-800 rounded-sm max-w-lg w-full p-6 space-y-4 shadow-2xl">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-1">
            // Regenerate channel kit
          </div>
          <h2 className="text-white text-xl font-black">
            {kit?.name || "Channel kit"}
          </h2>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            re-renders avatar + both banners · 30–60 seconds
          </p>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-black/40 p-3">
          <StylePresetPicker
            value={stylePreset}
            onChange={setStylePreset}
            label="Visual style"
            helpText="Applied to both banners and the avatar on this regenerate."
            testId="regen-style-preset"
          />
        </div>

        <div className="border border-zinc-800 rounded-sm bg-black/40 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
            Banner image model
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              data-testid="regen-banner-provider-openai"
              onClick={() => setBannerProvider("openai")}
              className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                bannerProvider === "openai"
                  ? "border-yellow-400 bg-yellow-400/10 text-white"
                  : "border-zinc-700 bg-black/20 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
                OpenAI · gpt-image-1
              </div>
              <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                Volumetric CGI / Pixar 3D
              </div>
            </button>
            <button
              type="button"
              data-testid="regen-banner-provider-gemini"
              onClick={() => setBannerProvider("gemini")}
              className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${
                bannerProvider === "gemini"
                  ? "border-yellow-400 bg-yellow-400/10 text-white"
                  : "border-zinc-700 bg-black/20 text-zinc-400 hover:border-zinc-600"
              }`}
            >
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] font-bold">
                Gemini · Nano Banana
              </div>
              <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                Native 16:9 · flatter 2D feel
              </div>
            </button>
          </div>
        </div>

        <div className="border border-zinc-800 rounded-sm bg-black/40 p-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 flex items-center justify-between">
            <span>Cast for this kit (optional)</span>
            <Link
              to="/characters"
              className="text-yellow-400 hover:text-yellow-300"
            >
              Manage →
            </Link>
          </div>

          {loading ? (
            <div className="font-mono text-[10px] text-zinc-500">
              [recon] loading characters<span className="blink">_</span>
            </div>
          ) : characters.length === 0 ? (
            <div className="text-xs text-zinc-500 leading-relaxed">
              You haven&apos;t added any characters yet. Build a reusable cast on
              the{" "}
              <Link to="/characters" className="text-yellow-400 underline hover:text-yellow-300">
                Characters page
              </Link>{" "}
              first, then come back here to attach them. Otherwise, click Regenerate
              to re-render with the existing prompts only.
            </div>
          ) : (
            <>
              <p className="text-xs text-zinc-500 mb-3 leading-relaxed">
                Pick the recurring characters you want featured on the avatar +
                banners. Their descriptions get injected into the prompts so
                they render consistently.
              </p>
              <div className="flex flex-wrap gap-2">
                {characters.map((c) => {
                  const checked = picked.includes(c.character_id);
                  const firstImg = (c.images || [])[0];
                  return (
                    <button
                      key={c.character_id}
                      type="button"
                      onClick={() => togglePick(c.character_id)}
                      data-testid={`regen-pill-${c.character_id}`}
                      className={
                        "flex items-center gap-2 px-3 py-1.5 rounded-sm border text-xs transition-colors " +
                        (checked
                          ? "border-yellow-400 bg-yellow-400/10 text-yellow-300"
                          : "border-zinc-700 text-zinc-300 hover:border-yellow-400/60 hover:text-yellow-400")
                      }
                    >
                      {firstImg && (
                        <img
                          src={characterImageUrl(c.character_id, firstImg.image_id)}
                          alt=""
                          className="w-5 h-5 rounded-sm object-cover"
                        />
                      )}
                      <span className="font-bold">{c.name}</span>
                      {c.role && (
                        <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                          {c.role}
                        </span>
                      )}
                      {checked && <span className="text-yellow-400 font-bold">✓</span>}
                    </button>
                  );
                })}
              </div>
              {pickedSummary && (
                <div className="mt-2 font-mono text-[10px] text-zinc-600">
                  Will use: <span className="text-zinc-300">{pickedSummary}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="regen-cancel-btn"
            className="font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 border border-zinc-700 text-zinc-400 rounded-sm hover:border-zinc-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || loading}
            data-testid="regen-confirm-btn"
            className="font-mono text-[10px] uppercase tracking-[0.25em] px-4 py-2 bg-yellow-400 text-black font-bold rounded-sm hover:bg-yellow-300 disabled:opacity-50"
          >
            {submitting ? "Starting…" : "↻ Regenerate"}
          </button>
        </div>
      </div>
    </div>
  );
}
